import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { deriveLiveSessionState, LiveSessionState } from '@onesource/schemas';
import { getMediaUrl, useGetEvent, useGetMembersInstructors } from '@onesource/services';
import { downloadCalendarEvent, googleCalendarUrl, outlookCalendarUrl } from '@onesource/utils';
import type { TCalendarEvent } from '@onesource/utils';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { MdCalendarToday, MdChevronRight, MdEventAvailable, MdHourglassEmpty, MdSensors } from 'react-icons/md';
import { PiMicrosoftOutlookLogoBold } from 'react-icons/pi';
import { SiApple, SiGooglecalendar } from 'react-icons/si';
import { TbClock } from 'react-icons/tb';

import { LiveSessionCardSkeleton } from './live-session-card-skeleton';

interface LiveSessionCardProps {
  contentBlockId: string;
  /** Banner configured on the content block; falls back to a branded placeholder. */
  bannerUrl?: string | null;
  instructorId?: string | null;
}

const BRAND_RED = '#ff073b';
const BRAND_CORAL = '#ff7262';
const META_GREY = '#6b6b6b';

const formatCountdown = (remainingMs: number): string | null => {
  if (remainingMs <= 0) {
    return null;
  }

  const totalMinutes = Math.floor(remainingMs / 60_000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  // Seconds only matter once the wait is short enough for them to be meaningful.
  if (totalMinutes < 60) {
    const seconds = Math.floor((remainingMs % 60_000) / 1000);
    return `Starts in ${minutes}m ${seconds}s`;
  }

  return days > 0 ? `Starts in ${days}d ${hours}h ${minutes}m` : `Starts in ${hours}h ${minutes}m`;
};

const MetaChip = ({ icon, label }: { icon: React.ReactNode; label: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', color: META_GREY }}>
    {icon}
    <Typography sx={{ fontSize: '14px', lineHeight: '19px', color: META_GREY, whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
  </Box>
);

export const LiveSessionCard = ({ contentBlockId, bannerUrl, instructorId }: LiveSessionCardProps) => {
  // The card only ever renders on the lesson page, so the current path is exactly
  // where the meeting view should return the member to when they leave.
  const lessonPath = window.location.pathname;
  const { data: event, isLoading } = useGetEvent(contentBlockId);
  const { data: instructorsData } = useGetMembersInstructors();
  const [calendarAnchor, setCalendarAnchor] = useState<null | HTMLElement>(null);

  const instructor = instructorsData?.items?.find(i => String(i.id) === instructorId);
  const instructorName = instructor ? `${instructor.firstName} ${instructor.lastName}` : '';

  const startsAt = useMemo(() => (event ? new Date(event.startTime) : null), [event]);

  // Every transition is time-based, so the card keeps its own clock: the countdown
  // reaching zero opens the join window, and the join window closing switches to
  // the replay — both without the member having to refresh.
  const [nowMs, setNowMs] = useState(() => Date.now());
  // Derived without a recording id on purpose: the replay is rendered by the lesson
  // view (which classifies the block as video), never by this card, so the card only
  // ever needs PRE_LIVE -> READY_TO_JOIN -> PROCESSING.
  const state = event ? deriveLiveSessionState({ ...event, vimeoVideoId: null }, new Date(nowMs)) : null;

  // Once a session is over (or cancelled) nothing changes again, so stop ticking.
  const isSettled =
    state === null ||
    state === LiveSessionState.REPLAY ||
    state === LiveSessionState.PROCESSING ||
    state === LiveSessionState.CANCELLED;

  useEffect(() => {
    if (isSettled) {
      return;
    }
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isSettled]);

  const countdown =
    state === LiveSessionState.PRE_LIVE && startsAt ? formatCountdown(startsAt.getTime() - nowMs) : null;

  if (isLoading) {
    return <LiveSessionCardSkeleton />;
  }

  if (!event || !startsAt || !state || state === LiveSessionState.CANCELLED) {
    return null;
  }

  const endsAt = new Date(startsAt.getTime() + event.durationMinutes * 60_000);
  const canJoin = state === LiveSessionState.READY_TO_JOIN;

  // The meeting runs on a dedicated cross-origin-isolated document — that isolation
  // is what gives the Zoom SDK SharedArrayBuffer, and with it gallery view and
  // multi-spotlight. The main app can't be isolated (cross-origin fonts and CDN
  // images), so joining hands off to that page and returns here on leave.
  const meetingHref = `/meeting.html?c=${encodeURIComponent(contentBlockId)}&return=${encodeURIComponent(lessonPath)}`;

  /**
   * Opened with `window.open` rather than a `target="_blank"` link for a specific
   * reason: only a script-opened window is allowed to call `window.close()` on
   * itself, which is how the meeting page sends the member back when they leave.
   * A blank-target link would leave them stranded on a tab that cannot close.
   *
   * The lesson stays put in this tab, so leaving the meeting never costs them
   * their place.
   *
   * Deliberately no `noopener`: that makes `window.open` return null, which would
   * make the popup-blocked check below fire on every successful open and bounce
   * the member into this tab anyway. The target is our own same-origin page, so
   * handing it an `opener` costs nothing.
   */
  const openMeetingWindow = () => {
    const opened = window.open(meetingHref, '_blank');
    // Popup blocked (rare from a click, but possible): fall back to this tab
    // rather than appearing to do nothing.
    if (!opened) {
      window.location.href = meetingHref;
    }
  };

  // One description of the session, handed to whichever calendar the member picks.
  const calendarEvent: TCalendarEvent = {
    id: `${event.id}@onesource`,
    title: event.title,
    startsAt,
    durationMinutes: event.durationMinutes,
    url: `${window.location.origin}${lessonPath}`,
  };

  const closeCalendarMenu = () => setCalendarAnchor(null);

  // Google and Outlook take the member to their web calendar with the event
  // pre-filled; Apple (and any desktop client) goes through the .ics file, which
  // is the only thing those clients can consume.
  const openWebCalendar = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    closeCalendarMenu();
  };

  const downloadIcs = () => {
    downloadCalendarEvent(calendarEvent, `${event.title.replace(/[^\w-]+/g, '-').toLowerCase()}.ics`);
    closeCalendarMenu();
  };

  const brandGradient = `linear-gradient(90deg, ${BRAND_RED} 0%, ${BRAND_CORAL} 100%)`;

  const gradientButtonSx = {
    height: 32,
    minWidth: 'auto',
    flexShrink: 0,
    px: '12px',
    borderRadius: '8px',
    fontSize: '12px',
    lineHeight: '18px',
    fontWeight: 700,
    textTransform: 'none',
    color: 'common.white',
    whiteSpace: 'nowrap',
    background: brandGradient,
    // MUI gives endIcon a -4px right margin, which pushes the chevron past the
    // button's rounded edge; restore the design's 10px gap instead.
    '& .MuiButton-endIcon': { ml: '10px', mr: 0 },
    '&:hover': { background: brandGradient, opacity: 0.9 },
  };

  const renderAction = () => {
    if (canJoin) {
      return (
        <Button
          variant="contained"
          disableElevation
          onClick={openMeetingWindow}
          endIcon={<MdChevronRight size={16} />}
          sx={gradientButtonSx}
        >
          Join Live Meeting
        </Button>
      );
    }

    if (state === LiveSessionState.PRE_LIVE) {
      return (
        <>
          <Button
            variant="contained"
            disableElevation
            onClick={clickEvent => setCalendarAnchor(clickEvent.currentTarget)}
            endIcon={<MdEventAvailable size={16} />}
            sx={gradientButtonSx}
          >
            Add to Calendar
          </Button>
          <Menu
            anchorEl={calendarAnchor}
            open={!!calendarAnchor}
            onClose={closeCalendarMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => openWebCalendar(googleCalendarUrl(calendarEvent))}>
              <ListItemIcon>
                <SiGooglecalendar size={16} />
              </ListItemIcon>
              Google Calendar
            </MenuItem>
            <MenuItem onClick={() => openWebCalendar(outlookCalendarUrl(calendarEvent))}>
              <ListItemIcon>
                <PiMicrosoftOutlookLogoBold size={16} />
              </ListItemIcon>
              Outlook
            </MenuItem>
            <MenuItem onClick={downloadIcs}>
              <ListItemIcon>
                <SiApple size={16} />
              </ListItemIcon>
              Apple Calendar (.ics)
            </MenuItem>
          </Menu>
        </>
      );
    }

    // Processing: the session is over and there is nothing to click yet.
    return (
      <Typography sx={{ fontSize: '12px', lineHeight: '18px', fontWeight: 700, color: META_GREY }}>
        Replay coming soon
      </Typography>
    );
  };

  const resolvedBanner = bannerUrl ? getMediaUrl(bannerUrl) : '';

  // Sessions are authored in ET (the dashboard form and Zoom payload), but members
  // are spread across zones, so the card shows each member their own local time —
  // no mental arithmetic to work out when to show up. The zone is spelled out so
  // the time is never ambiguous, and `ZZZZ` resolves DST on its own.
  const startLocal = DateTime.fromJSDate(startsAt);
  const endLocal = DateTime.fromJSDate(endsAt);
  const dateLabel = startLocal.toFormat('LLLL d, yyyy');
  const timeLabel = `${startLocal.toFormat('h:mm')} - ${endLocal.toFormat('h:mm a')} ${endLocal.toFormat('ZZZZ')}`;

  return (
    <Box
      sx={{
        bgcolor: 'common.white',
        border: '1px solid',
        borderColor: canJoin ? BRAND_RED : BRAND_CORAL,
        // The live state gets the glow so it reads as urgent in a long lesson page.
        boxShadow: canJoin ? `0px 0px 7.5px ${BRAND_RED}80` : 'none',
        borderRadius: '15px',
        p: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '15px',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', sm: 182 },
            height: 102,
            flexShrink: 0,
            borderRadius: '8px',
            overflow: 'hidden',
            // Without a banner the tile carries the brand gradient rather than a
            // dead grey rectangle, so an unconfigured session still looks intentional.
            background: resolvedBanner ? 'transparent' : brandGradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {resolvedBanner ? (
            <Box
              component="img"
              src={resolvedBanner}
              alt={event.title}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <MdSensors size={32} color="#ffffff" />
          )}
        </Box>

        {/* Fixed to the thumbnail's height so the meta row and button sit on the
            card's bottom edge, as they do in the design. */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: '7px',
            flex: 1,
            minWidth: 0,
            minHeight: { sm: 102 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MdSensors size={16} color={BRAND_RED} />
            <Typography sx={{ fontSize: '12px', lineHeight: '18px', letterSpacing: '2.76px', color: BRAND_RED }}>
              LIVE CLASS
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: '16px', lineHeight: '22px', fontWeight: 700, color: 'common.black' }}>
              {event.title}
            </Typography>
            {instructorName && (
              <Typography sx={{ fontSize: '14px', lineHeight: '19px', color: 'common.black' }}>
                {instructorName}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <MetaChip icon={<MdCalendarToday size={16} />} label={dateLabel} />
              <MetaChip icon={<TbClock size={18} />} label={timeLabel} />
              {countdown && <MetaChip icon={<MdHourglassEmpty size={16} />} label={countdown} />}
            </Box>

            {renderAction()}
          </Box>
        </Box>
      </Box>

      {/* Shown in every state. It was gated on PRE_LIVE, which meant the agenda
          vanished the moment the join window opened — and stayed gone through the
          live and the replay, when it is just as useful to read. */}
      {event.description && (
        <Typography sx={{ fontSize: '14px', lineHeight: '19px', color: META_GREY, mt: 2, whiteSpace: 'pre-line' }}>
          {event.description}
        </Typography>
      )}
    </Box>
  );
};
